import type { ReactNode } from "react";

import { formatPurchaseOrderTimestamp } from "./mrpPurchaseOrderTimestamp.js";

const EM_DASH = "\u2014";
const MAXIMUM_VISIBLE_GIPI_PEEK_LINES = 6;

export type GipiPeekLine = {
  sku: string;
  quantity: number;
  unit_price: number | null;
};

export type GipiPeekInvoice = {
  mv_number: string | null;
  mv_status: string | null;
  created_at: string | null;
  supplier_name: string | null;
  currency: string | null;
};

export type GipiPeekRow = {
  label: string;
  value: ReactNode;
};

function peekField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : EM_DASH;
}

function formatPeekQuantity(value: number): string {
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatPeekUnitPrice(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return EM_DASH;
  }
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const code = currency?.trim();
  return code ? `${code} ${formatted}` : formatted;
}

export function buildGipiPeekRows(
  invoice: GipiPeekInvoice,
  lines: readonly GipiPeekLine[],
  options?: { vendorInvoiceNumber?: string | null },
): GipiPeekRow[] {
  const rows: GipiPeekRow[] = [
    { label: "Document", value: peekField(invoice.mv_number) },
    { label: "Status", value: peekField(invoice.mv_status) },
    { label: "Vendor invoice", value: peekField(options?.vendorInvoiceNumber) },
    { label: "Created", value: formatPurchaseOrderTimestamp(invoice.created_at) },
    { label: "Supplier", value: peekField(invoice.supplier_name) },
  ];
  const visibleLines = lines.slice(0, MAXIMUM_VISIBLE_GIPI_PEEK_LINES);
  for (const line of visibleLines) {
    const quantity = formatPeekQuantity(line.quantity);
    const unitPrice = formatPeekUnitPrice(line.unit_price, invoice.currency);
    rows.push({
      label: line.sku.trim() || EM_DASH,
      value: `${quantity} @ ${unitPrice}`,
    });
  }
  if (lines.length > MAXIMUM_VISIBLE_GIPI_PEEK_LINES) {
    rows.push({
      label: "More lines",
      value: `+${String(lines.length - MAXIMUM_VISIBLE_GIPI_PEEK_LINES)} more`,
    });
  }
  return rows;
}
